import {Inject, Injectable} from '@angular/core';
import { Router } from '@angular/router';
import { FiltersService } from '../../services/filters.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { Role } from 'src/app/global/policy-types';
import { FEATURE_FLAGS, FeatureFlags } from 'src/app/config/AppInitializer.factory';
import { AuthorizationService } from '../../services/authorization.service';
import { NavigationMenu } from 'src/app/global/enums';

@Injectable({
  providedIn: 'root'
})
export class NavbarService {

  filters: ContractModel | undefined;
  private readonly sidebarMenus: NavigationMenu[] = [
    NavigationMenu.SmartPower,
    NavigationMenu.TotalValueDelivery,
    NavigationMenu.SolutionParticipationDetails,
    NavigationMenu.ServiceInsights,
    NavigationMenu.ActionCenter,
    NavigationMenu.CustomerOverview
  ];

  constructor(
    private _router: Router,
    private _filtersService : FiltersService,
    private _authorizationService: AuthorizationService,
    @Inject(FEATURE_FLAGS) private featureFlags: FeatureFlags
  ) {
    this._filtersService.selectedFiltersObservable.subscribe(f => {
      this.filters = f;
     });
  }

  private hasRole(role: Role) {
    return this._authorizationService.hasRole(role) || false;
  }

  /**
   * Check if filters have been applied and we're not on the filter page
   * @returns true if filters are applied and user is not on filter selection page
   */
  public areFiltersApplied(): boolean {
    return this.canShowSidebarControls();
  }

  public canShowSidebarControls(): boolean {
    if (this._router.url.includes('filter')) {
      return false;
    }

    return this.sidebarMenus.some(menu => this.canNavigateTo(menu));
  }

  public canNavigateTo(menu: NavigationMenu): boolean {
    if (this._router.url.includes('filter')) {
      // If we are on the filter page, we cannot determine access to the menus until the selected filters are applied
      return false;
    }

    if (this.isFilterIndependentMenu(menu)) {
      return this.canNavigateToFilterIndependentMenu(menu);
    }

    if (!this.filters) {
      // If filters are not set, we cannot determine access to the menus until the selected filters are applied
      return false;
    }

    switch (menu) {
      case NavigationMenu.SmartPower:
        if (!this.hasRole(Role.CorporateAccountManager)) {
          return false;
        }

        if(this.filters.division && this.filters.dataView) {
          if(this.filters.division === 'Institutional' && this.filters.dataView === 'Contract') {
            return true;
          }

        }
        //if division is not set, or is not 'Institutional' with 'Contract' view, set to false
        return false;

      case NavigationMenu.TotalValueDelivery:
        if (!this.hasRole(Role.CorporateAccountManager)) {
          return false;
        }

        // TVD is enabled for all divisions and regions, for unsupported regions an empty result is returned in the UI
        if(this.filters.division && this.filters.regions){
          // For FRS division, TVD is not available for EU region division.
          // Hide TVD if EU region is selected.
          if (this.filters.division === 'FRS' && this.filters.regions.includes('EU')) {
              return false;
          }

          //Note: Needs to be removed once we've enabled LA, AP and IMEA TVD on prod (US-622452)
          if (this.filters.division === 'Institutional') {
            const isSingleAP = this.filters.regions.length === 1 && this.filters.regions[0] === 'AP';
            const isSingleLA = this.filters.regions.length === 1 && this.filters.regions[0] === 'LA';
            const isSingleIMEA = this.filters.regions.length === 1 && this.filters.regions[0] === 'IMEA';
            if (isSingleAP && !this.featureFlags.enableTVDForAPInstitutional) {
              return false;
            }
            if (isSingleLA && !this.featureFlags.enableTVDForLAInstitutional) {
              return false;
            }
            if (isSingleIMEA && !this.featureFlags.enableTVDAndSPForIMEAInstitutional) {
              return false;
            }
          }

          return true;
        }

        // Unable to determine yet until division filter is applied
        return false;

      case NavigationMenu.SolutionParticipationDetails:
        if (!this.hasRole(Role.CorporateAccountManager)) {
          return false;
        }

        if (!this.filters.division) {
          // Unable to determine yet until division filter is applied
          return false;
        }

        return this.isSolutionParticipationEnabledForDivision();

      case NavigationMenu.ServiceInsights:
        if (!this.hasRole(Role.CorporateAccountManager)) {
          return false;
        }

        if (this.filters.division) {
          // Service Insights is available for all divisions except Distributor
          return this.filters.division !== 'Distributor';
        }

        // Unable to determine yet until division filter is applied
        return false;

      case NavigationMenu.ActionCenter:
        if (!this.hasRole(Role.CorporateAccountManager)) {
          return false;
        }

        if (!this.filters.division) {
          // Unable to determine yet until division filter is applied
          return false;
        }

        if (this.filters.division !== 'Institutional') {
          return false;
        }

        if (this.filters.dataView !== 'Contract') {
          return false;
        }

        if (this.filters.regions && this.filters.regions.length == 1 && this.filters.regions[0] !== 'NA') {
          return false;
        }

        return this.featureFlags.enableActionCenter;

      default:
        // Invalid menu option, assume no access
        console.warn(`Unknown menu option: ${menu}`);
        return false;
    }
  }

  private isFilterIndependentMenu(menu: NavigationMenu): boolean {
    return [NavigationMenu.CustomerOverview].includes(menu);
  }

  private canNavigateToFilterIndependentMenu(menu: NavigationMenu): boolean {
    switch (menu) {
      case NavigationMenu.CustomerOverview:
        return this.hasRole(Role.Customer);
      default:
        return false;
    }
  }

  /**
   * Check if Solution Participation is disabled for the selected region in Institutional division
   * Currently IMEA is not supported
  */
  private isSolutionParticipationUnsupportedInstitutionalRegion(): boolean {
    const unsupportedRegions = ['IMEA'];
    const selectedRegions = this.filters?.regions ?? [];

    // Check if any selected region is in the unsupported list
    return selectedRegions.some(region => unsupportedRegions.includes(region));
  }

  /**
   * Check if Solution Participation is enabled for the current division
   * Takes into account division-specific feature flags and region restrictions
  */
  private isSolutionParticipationEnabledForDivision(): boolean {
    const division = this.filters?.division;

    switch (division) {
      case 'Institutional':
        // AP region support requires enableAsiaPacificSolPartInstitutional to be enabled
        if (this.filters?.regions && this.filters.regions.includes('AP')) {
          return this.featureFlags.enableAsiaPacificSolPartInstitutional;
        }

        // LA region support requires enableLatinAmericaSolPartInstitutional to be enabled
        if (this.filters?.regions && this.filters.regions.includes('LA')) {
          return this.featureFlags.enableLatinAmericaSolPartInstitutional;
        }

        if(this.filters?.regions && this.filters.regions.includes('IMEA')) {
          return this.featureFlags.enableTVDAndSPForIMEAInstitutional;
        }

        // For other regions, Institutional is enabled by default
        return true;

      case 'QSR':
        // Return false if Solution Participation QSR feature flag is disabled, regardless of region or other flags
        if(!this.featureFlags.enableSolutionParticipationQsr) {
          return false;
        }

        // AP region support requires enableAsiaPacificSolPartQSR to be enabled
        if (this.filters?.regions && this.filters.regions.includes('AP')) {
          return this.featureFlags.enableAsiaPacificSolPartQSR;
        }

        // LA region support requires enableLatinAmericaSolPartQSR to be enabled
        if (this.filters?.regions && this.filters.regions.includes('LA')) {
          return this.featureFlags.enableLatinAmericaSolPartQSR;
        }

        return this.featureFlags.enableSolutionParticipationQsr;

      case 'FRS':
        // Return false if Solution Participation FRS feature flag is disabled, regardless of region or other flags
        if (!this.featureFlags.enableSolutionParticipationFrs) {
          return false;
        }

        // AP region support requires enableAsiaPacificSolPartFRS to be enabled
        if (this.filters?.regions && this.filters.regions.includes('AP')) {
          return this.featureFlags.enableAsiaPacificSolPartFRS;
        }

        // LA region support requires enableLatinAmericaSolPartFRS to be enabled
        if (this.filters?.regions && this.filters.regions.includes('LA')) {
          return this.featureFlags.enableLatinAmericaSolPartFRS;
        }

        return this.featureFlags.enableSolutionParticipationFrs;

      case 'Distributor':
      case 'Pest':
        return false;

      default:
        // Other divisions are enabled by default
        return true;
    }
  }
}
